import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import GridTable from '../../../common/GridTable';
import { tableHead } from '../../../common/tabeHelpers';
import DetailsColumn from '../../../pages/Inventory/reusableComponents/AddOption/AssetDetails';
import { actualValueForDropdown, validateStringValue } from '../../../utils/common';
import IconToolTip from '../../../common/IconToolTip';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { useForm } from 'react-hook-form';
import Select from '../../../common/Form/Select';
import { getDefultMilestone } from '../../../actions/admin';
import RenderContact from '../../../components/Inputs/Contact';
import Site from '../../../components/Inputs/Site';
import Warehouse from '../../../components/Inputs/Warehouse';
import Courier from '../../../components/Inputs/Courier';
import { MilestoneType } from '../../Ticket/TicketController';
import { createServiceRequest } from '../../../actions/service_request';
import { motion, AnimatePresence } from 'framer-motion';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import RenderRemarksInput from '../../../components/Inputs/RenderRemarksInput';
import { convertIntoPaisa } from '../../../utils/utils';

const workingConditionOptions = [
    {
        key: 'new',
        label: 'NEW',
        value: 'NEW',
    },
    {
        key: 'faulty',
        label: 'FAULTY',
        value: 'FAULTY',
    },
    {
        key: 'refurbished',
        label: 'REFURBISHED',
        value: 'REFURBISHED',
    },
];

const toDropdown = [
    { key: 1, value: 'SITE', label: 'Site' },
    { key: 2, value: 'WAREHOUSE', label: 'Warehouse' },
];

const formName = 'migrateAsset';
const MigrateAsset = ({ modalData, siteId, warehouseId, isBulk }) => {
    const dispatch = useDispatch();
    const defaultMilestones = useSelector((state) => state.admin.defaultMilestones)
    let source = {};

    useEffect(() => {
        dispatch(getDefultMilestone({}))
    }, [dispatch]);

    const [assetsData, setAssetsData] = useState(modalData.assetslist);

    useEffect(() => {
        setAssetsData(modalData.assetslist)
    }, [modalData])

    if (siteId > 0) source = { from: 'SITE', sourceId: siteId };
    if (warehouseId > 0) source = { from: 'WAREHOUSE', sourceId: warehouseId };
    const [from, setFrom] = useState(source.from);
    let sourceId;
    if (modalData && modalData.from === 'WAREHOUSE') setFrom('WAREHOUSE');
    if (source && !isNaN(source.sourceId) && source.sourceId > 0) sourceId = source.sourceId;

    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    const removeSelectedAssets = (id) => {
        if (!isNaN(id) && id > 0) {
            let selectedAssets = [...assetsData];
            let index = selectedAssets.findIndex((i) => parseInt(i.id) === parseInt(id));
            if (index !== -1) {
                selectedAssets.splice(index, 1);
            }
            setAssetsData(selectedAssets);
        }
    };

    const tableSchema = {
        table: [
            {
                head: tableHead('Asset Id'),
                body: {
                    render: ({ asset }) => {
                        return (
                            <>
                                {asset?.serial_number && (
                                    <IconToolTip placement={'bottom'} title={`Serial Number`}>
                                        <span className='text-lg font-medium text-gray-800'>{asset?.serial_number}</span>
                                    </IconToolTip>
                                )}
                            </>
                        );
                    },
                },
            },
            {
                head: tableHead('Asset Type'),
                body: {
                    render: ({ asset }) => {
                        return <p className="text-gray-700">{asset?.asset_type ? asset.asset_type?.title : '-'}</p>;
                    },
                },
            },
            {
                head: tableHead('Asset Details'),
                body: {
                    render: ({ asset }) => {
                        return (
                            <DetailsColumn
                                make={asset?.make}
                                model={asset?.model}
                                partcode={asset?.partcode}
                            />
                        );
                    },
                },
            },
            {
                head: {
                    headName: 'Actions',
                    render: (headName) => headName,
                    width: 0.5,
                },
                body: {
                    render: (item) => {
                        return (
                            <motion.span
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                    removeSelectedAssets(item.id);
                                }}
                                className="material-icons text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                                title="Remove asset"
                            >
                                delete
                            </motion.span>
                        );
                    },
                },
            },
        ],
    };

    const defaultValues = {
        from: warehouseId > 0 ? toDropdown[1] : toDropdown[0],
        sourceId,
        to: from,
        destinationId: undefined,
    };

    const methods = useForm({ defaultValues, mode: 'all' });
    const { watch, setValue, formState } = methods;
    const toValue = actualValueForDropdown(watch('from'));
    const submitRef = useRef(null);

    const currentLocationId = useMemo((state) => {
        if (assetsData?.[0]) {
            return [assetsData?.[0]?.location_id]
        }
        return [];
    }, [assetsData]);

    const submit = (formValues) => {
        const findMilestone = defaultMilestones.find((milestone) => milestone.type === MilestoneType.LOGISTICS);
        let milestones = [];
        if (findMilestone) {
            findMilestone.dispatch_location_id = assetsData?.[0]?.location_id;
            findMilestone.receive_location_id = actualValueForDropdown(formValues.destinationId);
            findMilestone.title = 'Milestone 1';
            findMilestone.execution_flow_required = false;
            delete findMilestone.tasks;

            const docket = {};

            if (formValues.logistic_cost) docket.cost_in_paisa = convertIntoPaisa(formValues.logistic_cost);
            if (formValues.docketNumber) docket.docket_number = formValues.docketNumber;
            if (formValues.courier_vendor_id) docket.vendor_id = actualValueForDropdown(formValues.courier_vendor_id);
            if (Object.keys(docket).length > 0) findMilestone.docket = docket;

            milestones = [findMilestone];
        };

        const payload = {
            org_id: assetsData[0]?.org_id,
            description: '',
            category: '',
            source: "PLATFORM",
            priority: "LOW",
            milestones,
            title: `${assetsData.length} Asset(s) are/is migrated from ${validateStringValue(assetsData?.[0]?.location?.external_id)} to ${validateStringValue(formValues?.destinationId?.label)}`,
            issue_category: formValues.issue_category
        };

        if (assetsData && assetsData.length > 0) {
            payload.bulk_asset_ids = assetsData.map((elem) => elem.asset_id);
        }
        dispatch(createServiceRequest({ payload, formName, onSuccess: () => dispatch(closeModalAction()), refreshAsset: true }));
    };

    const isWarehouseDropdown = toValue === 'WAREHOUSE';
    const isAddNewSite = actualValueForDropdown(watch('destinationId')) === 'add-new-site';
    const isHandleHeldingSelected = actualValueForDropdown(watch('courier_vendor_id')) === 'HAND_HOLDING';

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                when: "beforeChildren"
            }
        },
        exit: {
            opacity: 0,
            transition: { duration: 0.2 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 300, damping: 24 }
        }
    };

    const fadeVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.4 } },
        exit: { opacity: 0, transition: { duration: 0.2 } }
    };


    return (
        <div className='h-[65vh] px-4 bg-white rounded-md relative flex flex-col overflow-hidden'>
            <div className='flex-1 overflow-y-auto'>
                <Form
                    methods={methods}
                    onSubmit={submit}
                    submitRef={submitRef}
                >
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {!isBulk ? <motion.div
                            variants={itemVariants}
                            className='bg-blue-50 p-6 rounded-lg mb-6 border border-blue-100 shadow-sm'
                        >
                            <motion.h3
                                variants={itemVariants}
                                className='text-blue-800 font-medium mb-4 flex items-center text-lg'
                            >
                                <span className='material-icons mr-2 text-blue-600'>inventory_2</span>
                                Current Asset Information
                            </motion.h3>
                            <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
                                {assetsData[0]?.asset?.asset_number && (
                                    <motion.div
                                        variants={itemVariants}
                                        className='bg-white p-4 rounded-md shadow-sm hover:shadow-md transition-shadow duration-300'
                                        whileHover={{ y: -2 }}
                                    >
                                        <p className='text-xs text-gray-500 uppercase font-semibold'>Asset Number</p>
                                        <p className='font-medium text-gray-800 mt-1'>{assetsData[0]?.asset?.asset_number}</p>
                                    </motion.div>
                                )}
                                {assetsData[0]?.asset?.serial_number && (
                                    <motion.div
                                        variants={itemVariants}
                                        className='bg-white p-4 rounded-md shadow-sm hover:shadow-md transition-shadow duration-300'
                                        whileHover={{ y: -2 }}
                                    >
                                        <p className='text-xs text-gray-500 uppercase font-semibold'>Serial Number</p>
                                        <p className='font-medium text-gray-800 mt-1'>{assetsData[0]?.asset?.serial_number}</p>
                                    </motion.div>
                                )}
                                {assetsData[0]?.asset?.external_id && (
                                    <motion.div
                                        variants={itemVariants}
                                        className='bg-white p-4 rounded-md shadow-sm hover:shadow-md transition-shadow duration-300'
                                        whileHover={{ y: -2 }}
                                    >
                                        <p className='text-xs text-gray-500 uppercase font-semibold'>External ID</p>
                                        <p className='font-medium text-gray-800 mt-1'>{assetsData[0]?.asset?.external_id}</p>
                                    </motion.div>
                                )}
                                {assetsData[0]?.asset?.asset_type?.title && (
                                    <motion.div
                                        variants={itemVariants}
                                        className='bg-white p-4 rounded-md shadow-sm hover:shadow-md transition-shadow duration-300'
                                        whileHover={{ y: -2 }}
                                    >
                                        <p className='text-xs text-gray-500 uppercase font-semibold'>Asset Type</p>
                                        <p className='text-gray-800 text-sm mt-1 line-clamp-2'>{assetsData[0]?.asset?.asset_type?.title}</p>
                                    </motion.div>
                                )}
                                {assetsData[0]?.asset?.make && (
                                    <motion.div
                                        variants={itemVariants}
                                        className='bg-white p-4 rounded-md shadow-sm hover:shadow-md transition-shadow duration-300'
                                        whileHover={{ y: -2 }}
                                    >
                                        <p className='text-xs text-gray-500 uppercase font-semibold'>Make</p>
                                        <p className='text-gray-800 text-sm mt-1 line-clamp-2'>{assetsData[0]?.asset?.make}</p>
                                    </motion.div>
                                )}
                                {assetsData[0]?.asset?.model && (
                                    <motion.div
                                        variants={itemVariants}
                                        className='bg-white p-4 rounded-md shadow-sm hover:shadow-md transition-shadow duration-300'
                                        whileHover={{ y: -2 }}
                                    >
                                        <p className='text-xs text-gray-500 uppercase font-semibold'>Model</p>
                                        <p className='text-gray-800 text-sm mt-1 line-clamp-2'>{assetsData[0]?.asset?.model}</p>
                                    </motion.div>
                                )}

                                {assetsData[0]?.asset?.location?.address && (
                                    <motion.div
                                        variants={itemVariants}
                                        className='bg-white p-4 rounded-md shadow-sm hover:shadow-md transition-shadow duration-300'
                                        whileHover={{ y: -2 }}
                                    >
                                        <p className='text-xs text-gray-500 uppercase font-semibold'>Current Location</p>
                                        <p className='text-gray-800 text-sm mt-1 line-clamp-2'>{assetsData[0]?.asset?.location?.address}</p>
                                    </motion.div>
                                )}
                            </div>
                        </motion.div> : null}

                        {isBulk ? <motion.div
                            variants={itemVariants}
                            className='bg-white p-6 rounded-lg border shadow-sm mb-6'
                        >
                            <motion.h3
                                variants={itemVariants}
                                className='text-lg font-medium text-gray-800 mb-4 flex items-center'
                            >
                                <span className='material-icons mr-2 text-gray-600'>inventory_2</span>
                                Assets to Migrate
                            </motion.h3>
                            <motion.div
                                variants={itemVariants}
                                className='rounded-md overflow-hidden shadow-md'
                            >
                                <GridTable
                                    isLoading={false}
                                    schema={tableSchema}
                                    rowData={assetsData}
                                    tableError={'No assets selected'}
                                    tableBodyHeight={'h-max'}
                                />
                            </motion.div>
                        </motion.div> : null}

                        <motion.div
                            variants={itemVariants}
                            className='mb-6 bg-white p-6 rounded-lg border shadow-sm'
                        >
                            <motion.h3
                                variants={itemVariants}
                                className='text-lg font-medium text-gray-800 mb-4 flex items-center'
                            >
                                <span className='material-icons mr-2 text-blue-600'>location_on</span>
                                Destination Details
                            </motion.h3>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4'>
                                <motion.div variants={itemVariants}>
                                    <Select
                                        label='Destination Type'
                                        required
                                        name='from'
                                        options={toDropdown}
                                        className='w-full'
                                        onChange={() => {
                                            setValue('destinationId', '')
                                        }}
                                    />
                                </motion.div>

                                <AnimatePresence>
                                    {!isWarehouseDropdown && (
                                        <motion.div
                                            variants={fadeVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <Site
                                                label='Destination Site'
                                                name='destinationId'
                                                required
                                                org_id={assetsData[0]?.org_id}
                                                filter={[...currentLocationId]}
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <AnimatePresence>
                                    {isAddNewSite && (
                                        <motion.div
                                            className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 p-4 bg-gray-50 rounded-md border border-gray-200 mt-2"
                                            variants={fadeVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <motion.h4
                                                className="col-span-2 text-md font-medium text-gray-700 mb-2"
                                                variants={itemVariants}
                                            >
                                                New Site Information
                                            </motion.h4>
                                            <motion.div variants={itemVariants}>
                                                <Input
                                                    required
                                                    name={`destinationId:site-external-id`}
                                                    label="Site ID"
                                                    placeholder="Enter site ID"
                                                />
                                            </motion.div>
                                            <motion.div variants={itemVariants}>
                                                <Input
                                                    required
                                                    name={`destinationId:site-branch-name`}
                                                    label="Branch Name"
                                                    placeholder="Enter branch name"
                                                />
                                            </motion.div>
                                            <motion.div variants={itemVariants} className="col-span-2">
                                                <Input
                                                    required
                                                    name={`destinationId:site-address`}
                                                    label="Address"
                                                    placeholder="Enter full address"
                                                />
                                            </motion.div>
                                            <motion.div variants={itemVariants} className="col-span-2">
                                                <RenderContact
                                                    required
                                                    name={`destinationId:site-contact`}
                                                    label="Contact Person"
                                                    org_id={assetsData[0]?.org_id}
                                                />
                                            </motion.div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <AnimatePresence>
                                    {isWarehouseDropdown && (
                                        <motion.div
                                            variants={fadeVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <Warehouse
                                                addNew={false}
                                                label='Destination Warehouse'
                                                name='destinationId'
                                                required
                                                org_id={assetsData[0]?.org_id}
                                                filter={[...currentLocationId]}
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <motion.div variants={itemVariants}>
                                    <RenderRemarksInput name='issue_category' label='Reason' category='ISSUE_CATEGORY' org_id={assetsData?.[0]?.org_id} required={false} />
                                </motion.div>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={itemVariants}
                            className='mb-6 bg-white p-6 rounded-lg border shadow-sm'
                        >
                            <motion.h3
                                variants={itemVariants}
                                className='text-lg font-medium text-gray-800 mb-4 flex items-center'
                            >
                                <span className='material-icons mr-2 text-blue-600'>local_shipping</span>
                                Logistics Information
                            </motion.h3>
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4'>
                                <motion.div variants={itemVariants}>
                                    <Courier
                                        name='courier_vendor_id'
                                        label='Courier Vendor'
                                        prefill={['HAND_HOLDING']}
                                    />
                                </motion.div>

                                <AnimatePresence>
                                    {!isHandleHeldingSelected && (
                                        <>
                                            <motion.div
                                                variants={fadeVariants}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                            >
                                                <Input
                                                    label='Docket Number'
                                                    name='docketNumber'
                                                    placeholder="Enter tracking number"
                                                    shouldUnregister
                                                />
                                            </motion.div>
                                            <motion.div
                                                variants={fadeVariants}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                            >
                                                <Input
                                                    step='any'
                                                    label='Logistics Cost'
                                                    name='logistic_cost'
                                                    type='number'
                                                    placeholder="0.00"
                                                    shouldUnregister
                                                />
                                            </motion.div>
                                        </>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>

                        <motion.div
                            variants={itemVariants}
                            className='mb-6 bg-gray-50 p-6 rounded-lg border shadow-sm'
                        >
                            <motion.h3
                                variants={itemVariants}
                                className='text-lg font-medium text-gray-800 mb-4 flex items-center'
                            >
                                <span className='material-icons mr-2 text-blue-600'>summarize</span>
                                Migration Summary
                            </motion.h3>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                <motion.div
                                    variants={itemVariants}
                                    className='bg-white p-4 rounded-md shadow-sm'
                                >
                                    <h4 className='text-sm font-semibold text-gray-600 mb-2'>Source</h4>
                                    <p className='text-gray-800'>{from === 'SITE' ? 'Site' : 'Warehouse'}: {assetsData?.[0]?.location?.address || 'N/A'}</p>
                                </motion.div>
                                <motion.div
                                    variants={itemVariants}
                                    className='bg-white p-4 rounded-md shadow-sm'
                                >
                                    <h4 className='text-sm font-semibold text-gray-600 mb-2'>Destination</h4>
                                    <p className='text-gray-800'>{toValue === 'SITE' ? 'Site' : 'Warehouse'}</p>
                                </motion.div>
                                <motion.div
                                    variants={itemVariants}
                                    className='bg-white p-4 rounded-md shadow-sm'
                                >
                                    <h4 className='text-sm font-semibold text-gray-600 mb-2'>Assets</h4>
                                    <p className='text-gray-800'>{assetsData.length} asset(s) selected for migration</p>
                                </motion.div>
                                <motion.div
                                    variants={itemVariants}
                                    className='bg-white p-4 rounded-md shadow-sm'
                                >
                                    <h4 className='text-sm font-semibold text-gray-600 mb-2'>Working Condition</h4>
                                    <p className='text-gray-800'>{assetsData?.[0]?.working_condition || 'Not specified'}</p>
                                </motion.div>
                            </div>
                        </motion.div>
                    </motion.div>
                </Form>
            </div>
            <div className='flex justify-end py-4 gap-4 border-t sticky bottom-0 bg-white z-10' style={{ height: '5rem' }}>
                <ButtonScogoPrimary
                    textOrComponent={'Submit Migration Request'}
                    loading={formButtonLoading[formName]}
                    onCancel={() => dispatch(closeModalAction())}
                    onClick={() => {
                        submitRef?.current?.click();
                    }}
                />
                <ButtonScogoClosedOutlined
                    onClick={() => dispatch(closeModalAction())}
                    textOrComponent={'Cancel'}
                />
            </div>
        </div>
    );
};

export default MigrateAsset;
